import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Users,
  Award,
  Clock,
  Heart,
  Scale,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

export default function LeanLifePage() {
  const { open: openAppointment } = useAppointment();
  const subspecialties = [
    {
      title: "Medical Weight Management",
      description: "Doctor-supervised weight loss for obesity & metabolic disorders.",
      image: "/MedicalWeightManagement.jpg",
    },
    {
      title: "Clinical Nutrition",
      description: "Personalized diet plans based on lifestyle, health status & goals.",
      image: "/ClinicalNutrition.jpg",
    },
    {
      title: "Metabolic Health & Wellness",
      description: "PCOS, thyroid imbalance, diabetes & cholesterol management.",
      image: "/MetabolicHealth&Wellness.jpg",
    },
    {
      title: "Lifestyle Medicine",
      description: "Sleep optimization, stress management & habit correction.",
      image: "/LifestyleMedicine.jpg",
    },
    {
      title: "Post-Pregnancy Weight Care",
      description: "Safe and structured weight loss for new mothers.",
      image: "/Post-PregnancyWeightCare.jpg",
    },
    {
      title: "Child & Adolescent Weight Management",
      description: "Healthy, age-appropriate weight control.",
      image: "/Child&AdolescentWeightManagement.jpg",
    },
  ];

  const programs = [
    "Personalized Weight Loss Programs",
    "Nutrition Plans for Diabetes, Thyroid, PCOS & Fatty Liver",
    "Body Composition Analysis (BCA)",
    "Detox & Metabolic Reset Diets",
    "Stress, Sleep & Lifestyle Coaching",
    "Healthy Weight Gain Programs",
    "Corporate Wellness & Health Optimization Plans",
    "Family Nutrition & Long-term Wellness Programs",
    "Meal Planning & Sustainable Habit Building",
  ];

  const whyChoose = [
    {
      icon: Users,
      title: "Medical Supervision",
      description: "Medically supervised weight loss programs by clinical nutritionists & physicians",
    },
    {
      icon: Award,
      title: "Personalized Plans",
      description: "Personalized and sustainable plans (no fad diets or extreme restrictions)",
    },
    {
      icon: Heart,
      title: "Metabolic Focus",
      description: "Focus on metabolic correction rather than temporary weight loss",
    },
    {
      icon: CheckCircle2,
      title: "Evidence-Based",
      description: "Evidence-based approach combining nutrition, fitness & lifestyle medicine",
    },
    {
      icon: Clock,
      title: "Continuous Support",
      description: "Regular tracking, motivation, and continuous progress monitoring",
    },
  ];

  const articles = [
    {
      title: "Why Fad Diets Don't Work: The Science of Sustainable Weight Loss",
      excerpt:
        "Discover why quick-fix diets fail and how sustainable, medically-supervised programs lead to lasting results.",
    },
    {
      title: "Nutrition Tips for PCOS, Thyroid & Hormonal Balance",
      excerpt: "Learn how proper nutrition can help manage hormonal imbalances and improve overall health.",
    },
    {
      title: "How to Boost Your Metabolism Naturally",
      excerpt: "Evidence-based strategies to enhance your metabolic rate through nutrition and lifestyle changes.",
    },
    {
      title: "Sleep, Stress & Weight Gain — The Hidden Connection",
      excerpt: "Understand the critical link between sleep quality, stress levels, and weight management success.",
    },
  ];

  const faqs = [
    {
      question: "Who can join the LeanLife program?",
      answer:
        "Anyone looking to lose weight, gain weight, manage metabolic conditions, or improve overall health can join the LeanLife program. Our team customizes plans for individuals with diabetes, PCOS, thyroid issues, and other metabolic conditions.",
    },
    {
      question: "Are diet plans customized?",
      answer:
        "Yes, every plan is fully personalized based on your lifestyle, medical conditions, body composition, and individual goals. We don't believe in one-size-fits-all approaches. Our clinical nutritionists create tailored programs that fit your unique needs.",
    },
    {
      question: "Is this program safe for people with diabetes or thyroid issues?",
      answer:
        "Absolutely — LeanLife specializes in metabolic correction for conditions like diabetes, PCOS, thyroid imbalance, and other metabolic disorders. All programs are medically supervised and designed to work in conjunction with your existing treatments.",
    },
  ];
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      "name": "LeanLife - MAMS Hospitals",
      "url": "https://mamshospitals.com/leanlife",
      "logo": "https://mamshospitals.com/MAMS-Logo.png",
      "image": "https://mamshospitals.com/LeanLife.jpg",
      "description": "LeanLife at MAMS Hospitals provides medically supervised weight loss, metabolic health correction, clinical nutrition, PCOS care, thyroid management, and lifestyle medicine programs in Hyderabad.",
      "medicalSpecialty": "Lifestyle Medicine",
      "telephone": "+91-7399999981",
      "priceRange": "$$",

      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Bachupally",
        "addressLocality": "Hyderabad",
        "addressRegion": "Telangana",
        "postalCode": "500090",
        "addressCountry": "IN"
      },

      "availableService": [
        {
          "@type": "MedicalTherapy",
          "name": "Weight Loss Programs"
        },
        {
          "@type": "MedicalTherapy",
          "name": "PCOS Nutrition Management"
        },
        {
          "@type": "MedicalTherapy",
          "name": "Thyroid & Metabolic Health Programs"
        },
        {
          "@type": "MedicalTherapy",
          "name": "Clinical Nutrition Counseling"
        },
        {
          "@type": "MedicalTherapy",
          "name": "Lifestyle & Wellness Coaching"
        }
      ],

      "sameAs": [
        "https://www.facebook.com/MAMSHospitals",
        "https://www.instagram.com/mams_hospitals"
      ]
    },

    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Who can join the LeanLife program?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Anyone looking for weight loss, metabolic correction, PCOS management, thyroid balance, or lifestyle improvement can join the LeanLife program."
          }
        },
        {
          "@type": "Question",
          "name": "Are diet plans customized?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, all LeanLife programs are fully personalized based on medical history, body composition, lifestyle, and individual goals."
          }
        },
        {
          "@type": "Question",
          "name": "Is LeanLife safe for diabetes and thyroid patients?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, LeanLife specializes in medically supervised metabolic correction for diabetes, thyroid disorders, obesity, and hormonal imbalance."
          }
        }
      ]
    },

    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "LeanLife - Weight Loss & Lifestyle Medicine Program",
      "url": "https://mamshospitals.com/leanlife",
      "description": "Best weight loss and metabolic health program in Hyderabad offering clinical nutrition, PCOS management, thyroid care, obesity treatment, and lifestyle medicine.",
      "keywords": [
        "Weight Loss Program Hyderabad",
        "Best Nutritionist Hyderabad",
        "PCOS Diet Hyderabad",
        "Thyroid Weight Loss Hyderabad",
        "Metabolic Health Hyderabad",
        "Clinical Nutrition Hyderabad",
        "Lifestyle Medicine Hyderabad"
      ]
    }
  ];
  return (
    <>
    <SEO
      title="Best Weight Loss & Metabolic Health Program in Hyderabad | LeanLife | MAMS Hospitals"
      description="LeanLife at MAMS Hospitals offers medically supervised weight loss, clinical nutrition, PCOS care, thyroid management, metabolic correction, and lifestyle medicine programs in Hyderabad."
      keywords="weight loss program Hyderabad,
    best nutritionist Hyderabad,
    clinical nutrition Hyderabad,
    PCOS diet Hyderabad,
    thyroid weight loss Hyderabad,
    metabolic health Hyderabad,
    obesity treatment Hyderabad,
    dietitian Hyderabad,
    lifestyle medicine Hyderabad,
    weight management Hyderabad,
    fat loss program Hyderabad,
    wellness program Bachupally,
    LeanLife MAMS Hospitals"
      schema={schema}
    />

    <div className="flex flex-col min-h-screen">
    {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Weight Management Program in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                LeanLife – Transform Your Health & Lifestyle
              </h1>

              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                LeanLife at MAMS Hospitals offers personalized, sustainable, and medically supervised programs
                for weight loss, metabolic health, and long-term wellness. Sustainable weight loss, better health,
                and a confident new you.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative mt-8 md:mt-0">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-4 sm:p-6 rounded-2xl shadow-xl z-10">
                <div className="text-3xl font-bold">100%</div>
                <div className="text-sm">Personalized Care</div>
              </div>

              <img
                src="/LeanLife.jpg"
                alt="LeanLife Weight Management Program"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/leanlife"
      />
            </div>
          </div>
        </div>
      </section>

      {/* SUB-SPECIALTIES */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Sub-Specialities in LeanLife
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive weight management and lifestyle medicine expertise
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((specialty, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-teal-500"
              >
                <CardContent className="p-0">
                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={specialty.image}
                      alt={specialty.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{specialty.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{specialty.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS & SERVICES */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Programs & Services Offered
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive programs tailored to your health goals
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {programs.map((program, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                <span className="text-gray-700 font-medium">{program}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose LeanLife at MAMS Hospitals
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Trusted expertise in weight management and metabolic health
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                    <item.icon className="h-7 w-7 text-teal-600" />
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-2">Support for PCOS, diabetes, thyroid, hormonal imbalance & obesity</p>
            <p className="text-gray-600">Conveniently located near Miyapur & Bachupally</p>
          </div>
        </div>
      </section>

      {/* MEET OUR SPECIALIST */}
      {/* <section className="py-16 md:py-20 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Meet Our Specialist</h2>
            <p className="text-lg text-gray-600">Expert guidance for sustainable lifestyle transformation</p>
          </div>

          <Card className="max-w-4xl mx-auto shadow-xl border-gray-200">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-8 items-center">
                <div className="flex justify-center">
                  <div className="relative">
                    <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-teal-500">
                      <img
                        src="/deepika-chalasani-nutritionist.jpg"
                        alt="Deepika Chalasani"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Deepika Chalasani</h3>

                  <p className="text-teal-600 font-semibold mb-4">
                    Clinical Nutritionist & Lifestyle Medicine Expert
                  </p>

                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Specialist in metabolic health, evidence-based weight loss, PCOS/thyroid nutrition &
                    sustainable lifestyle transformation. Deepika brings a holistic approach to helping clients
                    achieve lasting health improvements.
                  </p>

                  <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" onClick={openAppointment}>
                      Book Consultation
                    </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section> */}

      {/* RECENT ARTICLES */}
      {/* <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Recent Articles</h2>
            <p className="text-lg text-gray-600">Stay informed about weight management and wellness</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {articles.map((article, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-gray-200"
              >
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{article.excerpt}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQ SECTION */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-0 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Get answers to common questions about LeanLife</p>
          </div>

          <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left px-6 hover:text-teal-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      
      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department </p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
